import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { assessmentActions } from "../slices/assessment";
import { getErrorMessage } from "../../utils/errorUtils";
import { AssessmentRequestDto } from "../../shared/dtos/assessment/AssessmentRequestDto";
import { AssessmentResponseDto } from "../../shared/dtos/assessment/AssessmentResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export interface AssessmentQueryParams {
  page?: number;
  size?: number;
  classroomId?: string;
  termId?: string;
}

export const getAssessments =
  (params?: AssessmentQueryParams) => async (dispatch: AppDispatch) => {
    dispatch(assessmentActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<AssessmentResponseDto>>
      >("/assessments", {
        headers: { requiresAuth: true },
        params: params || { page: 1, size: 10 },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(assessmentActions.setItems(data.data.content));
        dispatch(assessmentActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(assessmentActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const createAssessment =
  (values: AssessmentRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(assessmentActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<AssessmentResponseDto>
      >("/assessments", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Assessment created successfully");
        dispatch(assessmentActions.createSuccess(data.data));
        dispatch(
          assessmentActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(assessmentActions.setErrorCreate(message));
    }
  };

export const updateAssessment =
  (payload: { id: string; values: AssessmentRequestDto }) =>
  async (dispatch: AppDispatch) => {
    dispatch(assessmentActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<AssessmentResponseDto>
      >(`/assessments/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Assessment updated");
        dispatch(assessmentActions.updateSuccess(data.data));
        dispatch(
          assessmentActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(assessmentActions.setErrorUpdate(message));
    }
  };

export const deleteAssessment =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(assessmentActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/assessments/${id}`,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Assessment deleted");
        dispatch(assessmentActions.deleteSuccess(id));
        dispatch(
          assessmentActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
      dispatch(assessmentActions.setErrorDelete(getErrorMessage(error)));
    }
  };

export const resetAssessmentServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(assessmentActions.setServerStatus(null));
};
